import React from 'react';
import { Shield } from 'lucide-react';
import { Button } from '../../ui/Button';

interface SessionsSettingsProps {
  sessionSettings: {
    sessionMaxDurationHours: number;
    sessionCleanupIntervalHours: number;
    maxConcurrentSessions: number;
    suspiciousActivityThreshold: number;
    sessionInactivityDays: number;
    enableSessionAnalytics: boolean;
    enableSessionFingerprinting: boolean;
    enableGeoTracking: boolean;
  };
  isSessionSettingsSaving: boolean;
  onSessionSettingsChange: (field: string, value: any) => void;
  onSave: () => void;
  onReset: () => void;
}

export const SessionsSettings: React.FC<SessionsSettingsProps> = ({
  sessionSettings,
  isSessionSettingsSaving,
  onSessionSettingsChange,
  onSave,
  onReset
}) => {
  // Add CSS for hiding number input spinners
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .no-spinners::-webkit-outer-spin-button,
      .no-spinners::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
      }
      .no-spinners {
        -moz-appearance: textfield !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h4 className="text-lg font-medium flex items-center" style={{ color: 'var(--color-text-primary)' }}>
          <Shield className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
          Session Management Settings
        </h4>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Configure user session behavior, security, and monitoring
        </p>
      </div>
      
      <div className="max-w-4xl space-y-8">
        {/* Session Duration and Cleanup */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Session Duration */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Session Duration (Hours)
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              How long sessions remain valid before expiring
            </p>
            <input
              type="number"
              min="1"
              max="8760"
              value={sessionSettings.sessionMaxDurationHours}
              onChange={(e) => onSessionSettingsChange('sessionMaxDurationHours', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 no-spinners"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                appearance: 'none'
              } as React.CSSProperties}
            />
          </div>

          {/* Cleanup Interval */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Cleanup Interval (Hours)
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              How often expired sessions are cleaned up (hours, 1-24)
            </p>
            <input
              type="number"
              min="1"
              max="24"
              step="1"
              value={sessionSettings.sessionCleanupIntervalHours}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                onSessionSettingsChange('sessionCleanupIntervalHours', value);
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 no-spinners"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                appearance: 'none'
              } as React.CSSProperties}
            />
          </div>
        </div>

        {/* Session Limits and Security */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Max Concurrent Sessions */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Max Concurrent Sessions
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              Maximum sessions per user
            </p>
            <input
              type="number"
              min="1"
              max="50"
              value={sessionSettings.maxConcurrentSessions}
              onChange={(e) => onSessionSettingsChange('maxConcurrentSessions', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 no-spinners"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                appearance: 'none'
              } as React.CSSProperties}
            />
          </div>

          {/* Suspicious Activity Threshold */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Suspicious Activity Threshold
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              Actions per session before flagging
            </p>
            <input
              type="number"
              min="10"
              max="10000"
              value={sessionSettings.suspiciousActivityThreshold}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 10;
                const clampedValue = Math.min(Math.max(value, 10), 10000);
                onSessionSettingsChange('suspiciousActivityThreshold', clampedValue);
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 no-spinners"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                appearance: 'none'
              } as React.CSSProperties}
            />
          </div>

          {/* Session Inactivity Days */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Session Inactivity (Days)
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              Days before inactive sessions expire
            </p>
            <input
              type="number"
              min="1"
              max="365"
              value={sessionSettings.sessionInactivityDays}
              onChange={(e) => onSessionSettingsChange('sessionInactivityDays', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 no-spinners"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                appearance: 'none'
              } as React.CSSProperties}
            />
          </div>
        </div>

        {/* Session Features */}
        <div className="space-y-4">
          <h5 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Session Features</h5>
          
          {/* Enable Session Analytics */}
          <div className="relative flex items-center justify-between p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border)'
          }}>
            <div>
              <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Enable Session Analytics</h6>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Collect and analyze session usage statistics
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sessionSettings.enableSessionAnalytics}
                onChange={(e) => onSessionSettingsChange('enableSessionAnalytics', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Enable Session Fingerprinting */}
          <div className="relative flex items-center justify-between p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border)'
          }}>
            <div>
              <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Enable Device Fingerprinting</h6>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Use device fingerprinting for enhanced security
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sessionSettings.enableSessionFingerprinting}
                onChange={(e) => onSessionSettingsChange('enableSessionFingerprinting', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Enable Geo Tracking */}
          <div className="relative flex items-center justify-between p-4 border rounded-lg" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border)'
          }}>
            <div>
              <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Enable Geo Tracking</h6>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Track approximate location based on IP address
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sessionSettings.enableGeoTracking}
                onChange={(e) => onSessionSettingsChange('enableGeoTracking', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Save and Reset Buttons */}
        <div className="flex justify-end space-x-3 mt-8">
          <Button
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onReset();
            }}
            size="sm"
            type="button"
          >
            Reset to Defaults
          </Button>
          <Button
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[SESSIONS SETTINGS] Save button clicked');
              console.log('[SESSIONS SETTINGS] Current settings:', sessionSettings);
              console.log('[SESSIONS SETTINGS] onSave function:', onSave);
              if (typeof onSave === 'function') {
                onSave();
              } else {
                console.error('[SESSIONS SETTINGS] onSave is not a function!', typeof onSave);
              }
            }}
            disabled={isSessionSettingsSaving}
            size="sm"
            type="button"
          >
            {isSessionSettingsSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};